/**
 * api.ts — base fetch wrapper for React code.
 *
 * Reads the server base URL from ConfigService (via angular-bridge) so there is
 * a single source of truth during the Angular → React migration.
 * Replace with env-var–based config once Angular is fully removed (Phase 6).
 */

import { getConfigService, getTokenService } from './angular-bridge';

// Shape of the server's JSON error body (mirrors the AngularJS $http interceptor's expectations).
export interface ApiErrorBody {
  errors?: Array<{ defaultMessage?: string }>;
  error?: string;
}

// Structured error thrown by every failed request so callers and the global error handler
// can branch on status / read server messages, rather than regex-parsing a string.
// status 0 means the request never reached the server (network failure / CORS / offline).
export class ApiError extends Error {
  constructor(
    readonly method: string,
    readonly path: string,
    readonly status: number,
    readonly body: string,
    readonly data?: ApiErrorBody,
  ) {
    super(`API ${method} ${path} → ${status}: ${body}`);
    this.name = 'ApiError';
  }
}

function buildUrl(path: string): string {
  const [pathname, existingQs] = path.split('?');
  const qs = new URLSearchParams(existingQs ?? '');
  const base = `${getConfigService().getServerBaseUrl()}${pathname}`;
  const qsStr = qs.toString();
  return qsStr ? `${base}?${qsStr}` : base;
}

function authHeaders(): Record<string, string> {
  const token = getTokenService().GetAccessToken?.();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(buildUrl(path), {
      method,
      headers: { 'Content-Type': 'application/json', ...authHeaders(), ...extraHeaders },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    // Request never reached the server (offline, DNS, CORS). Maps to Angular's status-0 branch.
    throw new ApiError(method, path, 0, e instanceof Error ? e.message : String(e));
  }

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    let data: ApiErrorBody | undefined;
    try {
      data = JSON.parse(text) as ApiErrorBody;
    } catch {
      // body was not JSON — leave data undefined, raw text is still on the error
    }
    throw new ApiError(method, path, res.status, text, data);
  }

  // 204 No Content or empty body
  const contentType = res.headers.get('content-type') ?? '';
  if (res.status === 204 || !contentType.includes('application/json')) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};
